// KEEP_IN_SYNC(weavy/core/src/modules/models/services/utils/parse-duration-seconds.util.ts)

const SECONDS_PER_UNIT: Record<string, number> = {
  s: 1,
  m: 60,
};

/**
 * Extracts a numeric duration in seconds from a model input object.
 *
 * Handles the heterogeneous duration formats across model DTOs:
 * - `duration: number` (Runway, Kling, LTX, MiniMax) — treated as seconds
 * - `duration: '8s'` (Google Veo 3) — numeric value with 's' (seconds) suffix
 * - `duration: '2m'` — numeric value with 'm' (minutes) suffix
 * - `duration: '5'` (Seedance) — bare numeric string, treated as seconds
 * - `seconds: string` (Sora) — same parsing rules
 *
 * Returns undefined when the value cannot be parsed to a positive number.
 */
export function parseDurationSeconds(input?: Record<string, unknown>): number | undefined {
  if (!input) return undefined;

  const raw = input.duration ?? input.seconds;
  if (raw == null) return undefined;

  if (typeof raw === 'number') {
    return raw > 0 ? raw : undefined;
  }

  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase();
    if (!normalized) return undefined;

    const match = normalized.match(/^(\d+(?:\.\d+)?)(s|m)?$/);
    if (!match) return undefined;

    const value = Number(match[1]);
    if (value <= 0) return undefined;

    const unit = match[2] as 's' | 'm';
    return value * (SECONDS_PER_UNIT[unit] ?? 1);
  }

  return undefined;
}
